import { useState } from "react";
import { generateExcludedRecordsWorkbook } from "../api/configurations";
import { reportToast } from "../../../../components/notifications/appToast";
import type { ReportConfig } from "../types";

// "Download Excluded Records": a companion to Download Report (useConfigurationSaveWorkflow),
// which is not changed. Downloads a workbook with one sheet per Excel-enabled metric listing
// the rows that share the metric's reporting period and health plan but fail at least one of
// its other conditions, in Full Row Detail form with an "Exclusion Reason" column.
export const useExcludedRecordsDownload = (errorMessage: (error: any, fallback: string) => any) => {
    const [excludedRecordsLoading, setExcludedRecordsLoading] = useState(false);
    const [excludedRecordsError, setExcludedRecordsError] = useState("");

    const downloadExcludedRecords = async (report: ReportConfig) => {
        if (excludedRecordsLoading) return;
        if (!report.reportId) {
            const message = "Report metadata has not loaded yet. Please wait before downloading.";
            setExcludedRecordsError(message);
            reportToast.warning("Excluded records cannot be generated yet", message);
            return;
        }

        setExcludedRecordsLoading(true);
        setExcludedRecordsError("");

        const toastId = reportToast.loading("Generating excluded records workbook...", `${report.label} - large reports can take several minutes.`);
        // The server only starts sending once the workbook is built; from then on, show how
        // much has arrived (throttled to whole megabytes so the toast doesn't re-render per chunk).
        let lastMb = -1;
        const onProgress = (loadedBytes: number) => {
            const mb = Math.floor(loadedBytes / (1024 * 1024));
            if (mb === lastMb) return;
            lastMb = mb;
            reportToast.loading("Downloading excluded records workbook...", `${report.label} - ${mb.toLocaleString()} MB received`, { id: toastId });
        };
        try {
            const response = await generateExcludedRecordsWorkbook(report.reportId, report.configVersionId, onProgress);
            const blob = response.data as Blob;
            const contentDisposition = response.headers?.["content-disposition"] || "";
            const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
            const fileName = fileNameMatch?.[1] || `${report.label || "Report"} - Excluded Records.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            reportToast.updateSuccess(toastId, "Excluded records downloaded", fileName);
        } catch (error) {
            console.log(error);
            let message = errorMessage(error, "Unable to generate the excluded records workbook.");
            const errorBlob = (error as any)?.response?.data;
            if (errorBlob instanceof Blob) {
                const serverMessage = (await errorBlob.text()).trim();
                if (serverMessage && serverMessage.length < 500 && !serverMessage.startsWith("<")) {
                    message = serverMessage.replace(/^"|"$/g, "");
                }
            }
            setExcludedRecordsError(message);
            reportToast.updateError(toastId, "Excluded records were not generated", message);
        } finally {
            setExcludedRecordsLoading(false);
        }
    };

    return { excludedRecordsLoading, excludedRecordsError, downloadExcludedRecords };
};
